import { SEC_ApiCall } from '../func.js';

export const IMAGE_MARKUP_MAX_IMAGES = 10;
export const IMAGE_MARKUP_MAX_MARKERS_PER_IMAGE = 20;
export const IMAGE_MARKUP_MAX_SOURCE_BYTES = 10 * 1024 * 1024;
export const IMAGE_MARKUP_MAX_EDGE = 2500;
export const IMAGE_MARKUP_JPEG_QUALITY = 0.85;

/** Load an image from a browser File or Blob and release its temporary URL. */
function loadImage(blob) {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(blob);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('The selected image could not be read.'));
        };
        image.src = objectUrl;
    });
}

/** Export a canvas as a JPEG File suitable for the shared upload hook. */
function canvasToJpegFile(canvas, fileName) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('The image could not be converted to JPEG.'));
                return;
            }
            resolve(new File([blob], fileName, { type: 'image/jpeg' }));
        }, 'image/jpeg', IMAGE_MARKUP_JPEG_QUALITY);
    });
}

/** Convert an administrator-selected JPG or PNG into the canonical stored JPEG. */
export async function normaliseSourceImage(file, imageId) {
    if (!file || !['image/jpeg', 'image/png'].includes(String(file.type || '').toLowerCase())) {
        throw new Error('Select a JPG or PNG image.');
    }
    if (file.size > IMAGE_MARKUP_MAX_SOURCE_BYTES) {
        throw new Error('Source images must be 10 MB or smaller.');
    }
    const image = await loadImage(file);
    const scale = Math.min(1, IMAGE_MARKUP_MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const jpegFile = await canvasToJpegFile(canvas, `form-location-${imageId}.jpg`);
    return {
        fileName: jpegFile.name,
        file: jpegFile,
        fileSize: jpegFile.size,
        previewUrl: URL.createObjectURL(jpegFile),
    };
}

/** Request a permission-checked inline URL for a stored source image. */
export async function getSourceImageUrl(formId, fileId) {
    return SEC_ApiCall('general/SignedURLs', {
        requestType: 'formsBuilder',
        locationId: formId,
        fileId,
        fileOldName: 'form-location.jpg',
        downloadMode: 'inline',
    });
}

/** Fetch a source image through its short-lived signed URL as a local Blob. */
export async function fetchSourceImageBlob(formId, fileId) {
    const signedUrl = await getSourceImageUrl(formId, fileId);
    const response = await fetch(signedUrl);
    if (!response.ok) throw new Error('The source image could not be loaded.');
    return response.blob();
}

/** Render every supplied red marker into a source image and return an upload wrapper. */
export async function createMarkedImageFile(sourceBlob, markers, displayName, imageId) {
    const image = await loadImage(sourceBlob);
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0);

    const markerList = Array.isArray(markers) ? markers : markers ? [markers] : [];
    const markerRadius = Math.max(10, Math.round(Math.min(canvas.width, canvas.height) * 0.018));
    markerList.forEach((marker) => {
        const markerX = Math.round(Number(marker.x) * canvas.width);
        const markerY = Math.round(Number(marker.y) * canvas.height);
        context.beginPath();
        context.arc(markerX, markerY, markerRadius, 0, Math.PI * 2);
        context.fillStyle = '#d71920';
        context.fill();
        context.lineWidth = Math.max(3, Math.round(markerRadius * 0.22));
        context.strokeStyle = '#ffffff';
        context.stroke();
    });

    const safeName = String(displayName || 'location').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'location';
    const jpegFile = await canvasToJpegFile(canvas, `${safeName}-marked.jpg`);
    return {
        fileName: jpegFile.name,
        file: jpegFile,
        fileSize: jpegFile.size,
        imageId,
        sourceImageName: displayName,
    };
}
